import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { bobbinApi, containersApi } from '../../../shared/lib/api'
import { useAuthStore } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import ReasonPicker from '../../../shared/components/ReasonPicker'
import {
  StepBar, FlowNote, InfoCard, BigButton, ScanZone,
  VarianceAlert, SuccessScreen, Divider, SectionHeading, LoadingSpinner, Badge
} from '../../../shared/components/UI'
import { clsx } from 'clsx'

const STEPS = ['Select Roll', 'Add Batches', 'Review', 'Close']

const WASTAGE_REASONS = [
  'Material breakage during process',
  'Shade mismatch — rejected',
  'Machine malfunction',
  'Operator handling loss',
  'Other (specify below)',
]

export default function BobbinPage() {
  const { user } = useAuthStore()
  const navigate  = useNavigate()
  const location = useLocation()
  
  const [step, setStep]               = useState(0)
  const [loading, setLoading]         = useState(true)
  const [availableRolls, setAvailableRolls] = useState([])
  const [selectedRoll, setSelectedRoll] = useState(location.state?.workOrder?.roll || null)
  
  const [batches, setBatches]         = useState([]) // Local display
  const [createdBatches, setCreatedBatches] = useState([]) // Backend batch data { batchId, totalWeightKg }
  
  const [containerInput, setContainerInput] = useState('')
  const [scanning, setScanning]       = useState(false)
  const [scannedContainer, setScannedContainer] = useState(null)
  
  const [newBobbins, setNewBobbins]   = useState('')
  const [newWeight, setNewWeight]     = useState('')
  const [showVariance, setShowVariance] = useState(false)
  const [wastageMode, setWastageMode] = useState(false)
  const [wastageReason, setWastageReason] = useState('')
  const [wastageNote, setWastageNote] = useState('')
  const [done, setDone]               = useState(false)
  const [error, setError]             = useState('')

  useEffect(() => {
    bobbinApi.availableRolls()
      .then(res => setAvailableRolls(res.data.data))
      .finally(() => setLoading(false))
  }, [])

  const totalBatchWeight = batches.reduce((s, b) => s + b.weight, 0)
  const originalWeight = selectedRoll ? parseFloat(selectedRoll.available_weight_kg || selectedRoll.net_weight_kg || 0) : 0
  const variance = originalWeight > 0
    ? (((originalWeight - totalBatchWeight) / originalWeight) * 100).toFixed(1)
    : 0

  const handleScanContainer = () => {
    if (!containerInput.trim()) return
    setScanning(true)
    setError('')
    containersApi.scan(containerInput.trim())
      .then(res => {
        setScannedContainer(res.data.data)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Container not found or invalid')
      })
      .finally(() => setScanning(false))
  }

  const { mutate: addBatchMutate, isPending: isAdding } = useMutation({
    mutationFn: (batchData) => bobbinApi.createBatch(batchData),
    onSuccess: (res) => {
      const b = res.data.data
      setCreatedBatches(prev => [...prev, { batchId: b.id, totalWeightKg: b.total_weight_kg }])
      setBatches(prev => [...prev, {
        container: scannedContainer.container_id,
        bobbins: parseInt(newBobbins),
        weight: parseFloat(newWeight),
      }])
      setNewBobbins('')
      setNewWeight('')
      setContainerInput('')
      setScannedContainer(null)
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to add batch')
    }
  })

  const addBatch = () => {
    if (!newBobbins || !newWeight || !scannedContainer) return
    addBatchMutate({
      rollId: selectedRoll.id,
      shadeId: selectedRoll.shade_id,
      containerId: scannedContainer.id,
      workOrderId: location.state?.workOrder?.id,
      bobbinCount: parseInt(newBobbins),
      totalWeightKg: parseFloat(newWeight),
      unitId: user.unit_id
    })
  }

  const { mutate: closeMutate, isPending: isClosing } = useMutation({
    mutationFn: () => bobbinApi.closeSession({
      rollId: selectedRoll.id,
      batches: createdBatches,
      wastageKg: parseFloat(variance) > 2 ? (originalWeight - totalBatchWeight).toFixed(2) : 0,
      wastageReason,
      wastageNote,
    }),
    onSuccess: () => setDone(true),
    onError: (err) => setError(err.response?.data?.message || 'Failed to close session')
  })

  const handleReview = () => {
    if (batches.length === 0) return
    setStep(2)
    if (parseFloat(variance) > 2) setShowVariance(true)
  }

  if (loading) return <TabletScreen><div className="p-12 text-center"><LoadingSpinner /></div></TabletScreen>

  if (done) return (
    <TabletScreen>
      <TabletHeader title="Bobbin & Batch" role="Metallic Master" unit={user?.unitName || 'Unit'} />
      <div className="p-4">
        <StepBar steps={STEPS} current={4} />
        <SuccessScreen title="Batch session closed" subtitle={`Roll ${selectedRoll?.id.substring(0,8)} fully processed`} />
        <div className="grid grid-cols-2 gap-3 mb-5">
          {[
            { label: 'Batches', value: batches.length },
            { label: 'Total bobbins', value: batches.reduce((s,b) => s + b.bobbins, 0) },
            { label: 'Batch weight', value: `${totalBatchWeight.toFixed(2)} KG` },
            { label: 'Wastage', value: `${(originalWeight - totalBatchWeight).toFixed(2)} KG` },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border border-gray-100 rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-1">{label}</p>
              <p className="text-lg font-semibold text-gray-800">{value}</p>
            </div>
          ))}
        </div>
        <BigButton variant="primary" onClick={() => navigate('/app/dashboard')}>Back to Dashboard</BigButton>
      </div>
    </TabletScreen>
  )

  return (
    <TabletScreen>
      <TabletHeader title="Bobbin & Batch" role="Metallic Master" unit={user?.unitName || 'Unit'} />
      <div className="p-4">
        <StepBar steps={STEPS} current={step} />

        {/* ── Step 0: Select Roll ── */}
        {step === 0 && (
          <>
            <SectionHeading>Available rolls in inventory</SectionHeading>
            <div className="flex flex-col gap-3 mb-5">
              {availableRolls.length === 0 && <p className="text-center text-gray-400 py-8">No rolls available in Unit {user?.unit?.name}</p>}
              {availableRolls.map((r) => (
                <div
                  key={r.id}
                  onClick={() => setSelectedRoll(r)}
                  className={clsx('select-card', selectedRoll?.id === r.id && 'select-card-active')}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-sm text-gray-800 font-mono">{r.id.substring(0,8)}</span>
                    <Badge variant={r.status === 'partial' ? 'amber' : 'green'}>{r.status}</Badge>
                  </div>
                  <p className="text-xs text-gray-500">Shade: {r.shade?.name || '—'}</p>
                  <p className="text-xs text-gray-500">Available: {r.available_weight_kg || r.net_weight_kg} KG</p>
                </div>
              ))}
            </div>
            <BigButton
              variant="success"
              disabled={!selectedRoll}
              onClick={() => setStep(1)}
            >
              {selectedRoll ? `Create Batches for ${selectedRoll.id.substring(0,8)}` : 'Select a roll to continue'}
            </BigButton>
            <BigButton variant="secondary" onClick={() => navigate('/app/dashboard')}>Back to Dashboard</BigButton>
          </>
        )}

        {/* ── Step 1: Add Batches ── */}
        {step === 1 && (
          <>
            <FlowNote>Roll {selectedRoll.id.substring(0,8)} · Max weight: {originalWeight} KG</FlowNote>

            {/* Batch list */}
            {batches.length > 0 && (
              <>
                <SectionHeading>Batches created this session</SectionHeading>
                <div className="flex flex-col gap-2 mb-3">
                  {batches.map((b, i) => (
                    <div key={i} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-teal-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">Batch {i + 1} · {b.container}</p>
                          <p className="text-xs text-gray-400">{b.bobbins} bobbins · {b.weight} KG</p>
                        </div>
                      </div>
                      <span className="badge badge-green">Saved</span>
                    </div>
                  ))}
                </div>
                <div className="bg-brand-50 rounded-xl px-4 py-3 mb-4 flex justify-between">
                  <span className="text-sm text-brand-700">Total batches weight</span>
                  <span className="text-sm font-semibold text-brand-700">{totalBatchWeight.toFixed(2)} KG</span>
                </div>
              </>
            )}

            {/* Add next batch */}
            <SectionHeading>Add next batch</SectionHeading>

            {!scannedContainer ? (
              <>
                <ScanZone title="Scan Container QR" subtitle="Scan the QR on the container first" />
                <div className="flex gap-2 mb-4">
                  <input
                    className="form-input flex-1 text-sm"
                    placeholder="Or type container ID"
                    value={containerInput}
                    onChange={(e) => setContainerInput(e.target.value)}
                  />
                  <button onClick={handleScanContainer} disabled={scanning} className="btn-primary px-4 rounded-lg text-sm font-medium">
                    {scanning ? '...' : 'Confirm'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="info-card mb-3" style={{ background: '#E8F7F2', borderColor: '#C2EFE0' }}>
                  <div className="info-row" style={{ borderBottom: 'none' }}>
                    <span className="info-label">Container</span>
                    <span className="info-value" style={{ color: '#0D7A5F' }}>{scannedContainer.container_id}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="form-label">No. of bobbins</label>
                    <input type="number" inputMode="numeric" className="form-input text-2xl text-center py-3 font-semibold" placeholder="0" value={newBobbins} onChange={(e) => setNewBobbins(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">Total weight (KG)</label>
                    <input type="number" inputMode="decimal" className="form-input text-2xl text-center py-3 font-semibold" placeholder="0.0" step="0.1" value={newWeight} onChange={(e) => setNewWeight(e.target.value)} />
                  </div>
                </div>
                <div className="flex gap-2 mb-4">
                  <BigButton variant="success" onClick={addBatch} disabled={isAdding} className="flex-1 py-3 text-base">
                    {isAdding ? 'Adding...' : '+ Add Batch'}
                  </BigButton>
                  <button onClick={() => setScannedContainer(null)} className="px-4 py-2 text-xs border border-gray-200 rounded-xl text-gray-500 bg-white">
                    Change
                  </button>
                </div>
              </>
            )}

            {error && <p className="text-red-500 text-xs mb-4">{error}</p>}

            <Divider />
            <BigButton variant="success" disabled={batches.length === 0} onClick={handleReview}>
              Done Adding — Review & Close
            </BigButton>
            <BigButton variant="secondary" onClick={() => setStep(0)}>Back — Change Roll</BigButton>
          </>
        )}

        {/* ── Step 2: Review ── */}
        {step === 2 && !wastageMode && (
          <>
            <SectionHeading>Session summary · {selectedRoll.id.substring(0,8)}</SectionHeading>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { label: 'Total batches',   value: batches.length },
                { label: 'Total bobbins',   value: batches.reduce((s,b) => s+b.bobbins,0) },
                { label: 'Original weight', value: `${originalWeight} KG` },
                { label: 'Batches weight',  value: `${totalBatchWeight.toFixed(2)} KG` },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white border border-gray-100 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">{label}</p>
                  <p className="text-lg font-semibold text-gray-800">{value}</p>
                </div>
              ))}
            </div>

            {error && <p className="text-red-500 text-xs mb-4">{error}</p>}

            {showVariance ? (
              <VarianceAlert
                pct={`${variance}%`}
                expectedKg={originalWeight}
                actualKg={totalBatchWeight.toFixed(2)}
              >
                <div className="grid grid-cols-2 gap-2">
                  <BigButton variant="warning" onClick={() => setWastageMode(true)} className="py-3 text-sm">
                    Log Wastage
                  </BigButton>
                  <BigButton variant="secondary" onClick={() => closeMutate()} disabled={isClosing} className="py-3 text-sm">
                    {isClosing ? '...' : 'Skip Wastage'}
                  </BigButton>
                </div>
              </VarianceAlert>
            ) : (
              <BigButton variant="success" onClick={() => closeMutate()} disabled={isClosing}>
                {isClosing ? 'Closing…' : 'Close Session'}
              </BigButton>
            )}
            <BigButton variant="secondary" onClick={() => setStep(1)}>Back — Add More Batches</BigButton>
          </>
        )}

        {/* ── Step 2b: Wastage reason ── */}
        {step === 2 && wastageMode && (
          <>
            <div className="info-card mb-4" style={{ background: '#FEF2F2', borderColor: '#FECACA' }}>
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <span className="info-label">Wastage to log</span>
                <span className="info-value" style={{ color: '#DC2626' }}>
                  {(originalWeight - totalBatchWeight).toFixed(2)} KG
                </span>
              </div>
            </div>
            <SectionHeading>Select reason</SectionHeading>
            <div className="mb-4">
              <ReasonPicker reasons={WASTAGE_REASONS} onChange={setWastageReason} />
            </div>
            <div className="mb-4">
              <label className="form-label">Additional notes (optional)</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder="Describe what happened…"
                value={wastageNote}
                onChange={(e) => setWastageNote(e.target.value)}
              />
            </div>
            <BigButton
              variant="danger"
              disabled={!wastageReason || isClosing}
              onClick={() => closeMutate()}
            >
              {isClosing ? 'Saving…' : 'Confirm & Log Wastage'}
            </BigButton>
            <BigButton variant="secondary" onClick={() => setWastageMode(false)}>Back</BigButton>
          </>
        )}
      </div>
    </TabletScreen>
  )
}
